/**
 * Domain utility functions for handling domain redirects
 */

/**
 * Gets the main application domain (complin.ai)
 */
export const getMainDomain = (): string => {
  if (typeof window === 'undefined') {
    return 'complin.ai';
  }
  
  const hostname = window.location.hostname;
  
  // If already on main domain, return it
  if (hostname === 'complin.ai' || hostname.startsWith('www.')) {
    return hostname.replace(/^www\./, '');
  }
  
  // For localhost and other cases, return as-is (for development)
  return hostname;
};

/**
 * Gets the protocol (http or https)
 */
export const getProtocol = (): string => {
  if (typeof window === 'undefined') {
    return 'https:';
  }
  return window.location.protocol;
};

/**
 * Constructs the full URL for the main domain
 */
export const getMainDomainUrl = (path: string = '/'): string => {
  const protocol = getProtocol();
  const domain = getMainDomain();
  
  // Ensure path starts with /
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  
  return `${protocol}//${domain}${normalizedPath}`;
};

/**
 * Checks if we're currently on the login page path
 * @deprecated No longer using subdomain - login is at /login path
 */
export const isOnLoginSubdomain = (): boolean => {
  // Always return false since we're using /login path instead of subdomain
  return false;
};

/**
 * Gets the login URL based on current origin
 * Returns current origin + /login (e.g., complin.ai/login or localhost:3000/login)
 * In production, always returns complin.ai/login even if on old subdomain
 */
export const getLoginUrl = (queryParams?: string): string => {
  if (typeof window === 'undefined') {
    return 'https://complin.ai/login';
  }
  
  const hostname = window.location.hostname;
  const protocol = window.location.protocol;
  const host = window.location.host; // host includes hostname:port
  const query = queryParams ? `?${queryParams}` : '';
  
  // In production, if on old login subdomain or main domain, always use complin.ai
  if (hostname.includes('complin.ai')) {
    return `https://complin.ai/login${query}`;
  }
  
  // In development (localhost), use current origin
  return `${protocol}//${host}/login${query}`;
};

/**
 * Redirects to the given path on the current domain
 * For root path '/' or '/login', redirects to '/home'
 */
/**
 * Full URL to accept an invite (token from Firestore invitation document).
 * Staff usernames do not receive email; the org shares this link manually.
 */
export const getAcceptInvitationUrl = (token: string): string => {
  const q = `token=${encodeURIComponent(token)}`;
  if (typeof window === 'undefined') {
    return `https://complin.ai/accept-invitation?${q}`;
  }
  const hostname = window.location.hostname;
  const protocol = window.location.protocol;
  const host = window.location.host;
  if (hostname.includes('complin.ai')) {
    return `https://complin.ai/accept-invitation?${q}`;
  }
  return `${protocol}//${host}/accept-invitation?${q}`;
};

export const redirectToMainDomain = (path: string = '/'): void => {
  if (typeof window === 'undefined') return;
  
  // Always redirect to /home for root or login paths (root is for marketing page)
  const normalizedPath = path === '/' || path === '/login' ? '/home' : path;
  
  console.log(`🔄 Redirecting to: ${normalizedPath}`);
  window.location.href = normalizedPath;
};
